import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navbar, Nav, Container, Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';
import UserMenu from './UserMenu';
import { canAccessModule } from '../utils/permissionUtils';
import { toast } from 'react-toastify';
import axios from 'axios';
import AllowanceBanner from './AllowanceBanner';
import HelpPane from './HelpPane';
import { getHelpContent } from './helpContent';
import { TAB_NAMES, MODULE_LABELS, helpTopicKey } from '../utils/menuMap';
import SidebarContext from '../contexts/SidebarContext';
import GlobalSearch from './GlobalSearch';
import useFindOnPage from '../hooks/useFindOnPage';

// Below Bootstrap's md breakpoint the sidebar is a drawer over the page instead of a column beside it
const PHONE_QUERY = '(max-width: 767.98px)';

const LOGO = 'https://s3.amazonaws.com/public.ibigdo.com/c/img/logo_ibigdo.png';
const LOGO_SMALL = 'https://s3.amazonaws.com/public.ibigdo.com/c/img/logo_ibigdo_small.png';

/** True while the screen matches the media query. */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setMatches(mq.matches);
    // Older Safari only has addListener; resize also covers browsers that don't report the change
    if (mq.addEventListener) mq.addEventListener('change', update); else mq.addListener(update);
    window.addEventListener('resize', update);
    update();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update); else mq.removeListener(update);
      window.removeEventListener('resize', update);
    };
  }, [query]);
  return matches;
}

function Layout({ children }) {
  useFindOnPage(); // arriving from the search box: scroll to the row and mark it
  const { user, storeEnabled, storeNewOrders, refreshStore } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [showTitleHelp, setShowTitleHelp] = useState(false);
  const { theme } = useTheme();

  // The count on Online Store: orders waiting to be confirmed, looked at again on each screen change and every 3 minutes
  useEffect(() => {
    if (!user || !storeEnabled) return undefined;
    refreshStore();
    const timer = setInterval(refreshStore, 3 * 60 * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, storeEnabled, location.pathname]);

  // The dot on the Help icon: replies from the team that the person has not opened yet
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = () => {
      axios.get('/api/feedback/unread-count')
        .then((r) => {
          if (cancelled) return;
          const count = Number(r.data?.count) || 0;
          setUnreadReplies(count);
          announceReplies(count);
        })
        .catch(() => {});
    };
    // Once per sign-in, a notice with a way to open them (the red count on the Help icon stays after it goes)
    const announceReplies = (count) => {
      if (count < 1 || window.location.pathname.startsWith('/help')) return;
      let signIn = '';
      try {
        signIn = (localStorage.getItem('token') || '').slice(-16);
        if (sessionStorage.getItem('repliesNoticeFor') === signIn) return;
        sessionStorage.setItem('repliesNoticeFor', signIn);
      } catch (e) {
        return; // no storage: skip rather than repeat on every screen
      }
      toast.info(
        <div>
          You have {count} new {count === 1 ? 'reply' : 'replies'} from support.
          <button
            type="button"
            className="btn btn-sm btn-primary ms-2"
            onClick={() => { toast.dismiss('new-replies'); navigate('/help/my-requests'); }}
          >
            Open
          </button>
        </div>,
        { toastId: 'new-replies', autoClose: 15000 }
      );
    };
    load();
    const timer = setInterval(load, 3 * 60 * 1000);
    const onSeen = () => setUnreadReplies(0);
    window.addEventListener('ibigdo:replies-seen', onSeen);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener('ibigdo:replies-seen', onSeen); };
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // The address is /module or /module/section
  const [, moduleSegment = '', sectionSegment = null] = location.pathname.split('/');
  const modulePath = `/${moduleSegment}`;
  const activeTab = sectionSegment;

  const isPhone = useMediaQuery(PHONE_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The one sidebar button: collapses the sidebar to icons on a wide screen, opens the drawer on a phone
  const toggleSidebar = useCallback(() => {
    if (isPhone) {
      setDrawerOpen((open) => !open);
    } else {
      setIsCollapsed((collapsed) => !collapsed);
    }
  }, [isPhone]);

  // Picking a page, or widening the window, closes the drawer
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => { if (!isPhone) setDrawerOpen(false); }, [isPhone]);

  // "[" toggles the sidebar, except while typing
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      e.preventDefault();
      toggleSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar]);

  const isActive = (path) => modulePath === path;

  const NAV_ITEMS = [
    { path: '/dashboard', label: MODULE_LABELS['/dashboard'], icon: 'bi-speedometer2', module: 'dashboard', color: '#3b82f6' },
    { path: '/account-management', label: MODULE_LABELS['/account-management'], icon: 'bi-briefcase', module: 'account-management', color: '#06b6d4' },
    { path: '/affiliate', label: MODULE_LABELS['/affiliate'], icon: 'bi-share', module: 'affiliate', color: '#10b981' },
    { path: '/sales', label: MODULE_LABELS['/sales'], icon: 'bi-graph-up-arrow', module: 'sales', color: '#22c55e', aliases: ['/customers', '/orders'] },
    { path: '/store', label: MODULE_LABELS['/store'], icon: 'bi-bag-heart', module: 'store', color: '#d946ef' },
    { path: '/purchase', label: MODULE_LABELS['/purchase'], icon: 'bi-truck', module: 'purchase', color: '#f97316' },
    { path: '/inventory', label: MODULE_LABELS['/inventory'], icon: 'bi-box-seam', module: 'inventory', color: '#a855f7' },
    { path: '/quality', label: MODULE_LABELS['/quality'], icon: 'bi-clipboard2-check', module: 'quality', color: '#14b8a6' },
    { path: '/production', label: MODULE_LABELS['/production'], icon: 'bi-gear-wide-connected', module: 'production', color: '#f59e0b' },
    { path: '/accounts', label: MODULE_LABELS['/accounts'], icon: 'bi-receipt', module: 'accounts', color: '#ec4899' },
    { path: '/org-admin', label: MODULE_LABELS['/org-admin'], icon: 'bi-building-gear', module: 'org-admin', color: '#6366f1' },
    { path: '/tech-support', label: MODULE_LABELS['/tech-support'], icon: 'bi-headset', module: 'tech-support', color: '#0ea5e9' },
    { path: '/system-admin', label: MODULE_LABELS['/system-admin'], icon: 'bi-shield-lock', module: 'system-admin', color: '#ef4444' },
    { path: '/settings', label: MODULE_LABELS['/settings'], icon: 'bi-gear', module: 'settings', color: '#64748b' }
  ];

  const isNavItemActive = (item) => {
    if (isActive(item.path)) return true;
    return (item.aliases || []).some((alias) => isActive(alias));
  };

  const getHeadingParts = () => {
    const path = modulePath;
    const headingMap = {
      '/dashboard': 'Dashboard',
      '/production': 'Production',
      '/sales': 'Sales',
      '/store': 'Online Store',
      '/customers': 'Sales',
      '/orders': 'Sales',
      '/users': 'Users',
      '/settings': 'Settings',
      '/help': 'Help & Support',
      '/roles': 'Role Management',
      '/org-admin': 'Organization Administration',
      '/system-admin': 'System Administration',
      '/tech-support': 'Tech Support',
      '/purchase': 'Purchase',
      '/inventory': 'Inventory',
      '/accounts': 'Accounts',
      '/quality': 'Quality',
      '/account-management': 'Account Management',
      '/affiliate': 'Affiliate'
    };
    
    const baseHeading = headingMap[path] || 'iBigDo';
    
    // Tab name mappings for modules with tabs
    const tabNameMap = TAB_NAMES;
    
    // Use state variable for active tab (which is updated via useEffect)
    const tabNames = tabNameMap[path];
    
    return { module: baseHeading, section: tabNames && activeTab ? tabNames[activeTab] || null : null };
  };

  const heading = getHeadingParts();
  // The help for the screen named in the title
  const helpTopic = heading.section ? getHelpContent(helpTopicKey(modulePath, activeTab)) : null;
  // Pages' own section lists collapse with the sidebar (not on a phone, where the sidebar is a drawer)
  const sidebarState = useMemo(() => ({ collapsed: isCollapsed && !isPhone }), [isCollapsed, isPhone]);
  const visibleItems = NAV_ITEMS.filter((item) => (item.module === null ? !user?.restricted : canAccessModule(user, item.module) && (item.module !== 'store' || storeEnabled)));
  const toggleLabel = isPhone ? 'Open menu' : (isCollapsed ? 'Expand sidebar' : 'Collapse sidebar');

  /** The module links; with labels hidden (collapsed) each shows its name beside it on hover. */
  const renderNavLinks = (iconsOnly) => visibleItems.map((item) => {
    const link = (
      <Nav.Link
        key={item.path}
        href={item.path}
        data-nav={item.module}
        style={{ '--icon-hover': item.color }}
        className={isNavItemActive(item) ? 'active' : ''}
        aria-current={isNavItemActive(item) ? 'page' : undefined}
        aria-label={iconsOnly ? item.label : undefined}
        onClick={(e) => { e.preventDefault(); navigate(item.path); }}
      >
        <i className={`bi ${item.icon} sidebar-nav-icon`} aria-hidden="true" />
        {!iconsOnly && <span className="sidebar-nav-label">{item.label}</span>}
        {item.module === 'store' && storeNewOrders > 0 && (
          <span className={`badge rounded-pill bg-danger ${iconsOnly ? 'position-absolute top-0 end-0' : 'ms-auto'}`} data-testid="store-new-orders"
            title={`${storeNewOrders} new online order${storeNewOrders === 1 ? '' : 's'} to confirm`}>{storeNewOrders > 9 ? '9+' : storeNewOrders}</span>
        )}
      </Nav.Link>
    );
    return iconsOnly ? (
      <OverlayTrigger key={item.path} placement="right" overlay={<Tooltip id={`nav-tip-${item.module}`}>{item.label}</Tooltip>}>
        {link}
      </OverlayTrigger>
    ) : link;
  });

  return (
    <div className="d-flex">
      {/* Sidebar: a column on a wide screen (full or icons only) */}
      <nav
        className={`sidebar d-none d-md-block${isCollapsed ? ' sidebar-collapsed' : ''}`}
        style={{ width: isCollapsed ? '4.5rem' : '15rem' }}
        aria-label="Main"
      >
        <div className="sidebar-inner">
          <div className="sidebar-brand">
            <img src={isCollapsed ? LOGO_SMALL : LOGO} alt="iBigDo" />
          </div>
          <Nav className="flex-column">{renderNavLinks(isCollapsed)}</Nav>
        </div>
      </nav>

      {/* ...and a drawer over the page on a phone */}
      <Offcanvas
        show={drawerOpen}
        onHide={() => setDrawerOpen(false)}
        placement="start"
        className="sidebar sidebar-drawer"
        data-bs-theme={theme}
        aria-label="Main menu"
      >
        <Offcanvas.Header closeButton closeLabel="Close menu">
          <img src={LOGO} alt="iBigDo" className="sidebar-drawer-logo" />
        </Offcanvas.Header>
        <Offcanvas.Body className="pt-0">
          <Nav className="flex-column">{renderNavLinks(false)}</Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <div className="flex-grow-1 min-w-0">
        {/* Top bar: sidebar button, where you are, then help, theme and your menu */}
        <Navbar className="border-bottom topbar" data-bs-theme={theme} bg="body-tertiary">
          <Container fluid className="flex-nowrap gap-2">
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="sidebar-toggle-tip">{isPhone ? 'Menu' : `${toggleLabel}  [`}</Tooltip>}
            >
              <button
                type="button"
                className="topbar-sidebar-toggle"
                onClick={toggleSidebar}
                aria-label={toggleLabel}
                aria-expanded={isPhone ? drawerOpen : !isCollapsed}
              >
                <i className={`bi ${isPhone ? 'bi-list' : 'bi-layout-sidebar'}`} aria-hidden="true" />
              </button>
            </OverlayTrigger>
            <h1 className="topbar-title">
              <span className="topbar-module">{heading.module}</span>
              {heading.section && (
                <>
                  <span className="topbar-crumb-sep" aria-hidden="true">/</span>
                  <span className="topbar-section">{heading.section}</span>
                </>
              )}
              {helpTopic && (
                <button
                  type="button"
                  className="topbar-help"
                  onClick={() => setShowTitleHelp(true)}
                  aria-label={`Help: ${helpTopic.title}`}
                  title={`Help: ${helpTopic.title}`}
                >
                  <i className="bi bi-question-circle" aria-hidden="true" />
                </button>
              )}
            </h1>
            {helpTopic && (
              <HelpPane show={showTitleHelp} onHide={() => setShowTitleHelp(false)} title={helpTopic.title}>
                {helpTopic.content}
              </HelpPane>
            )}
            <GlobalSearch />
            <Nav className="ms-auto flex-row align-items-center flex-nowrap gap-1">
              <Nav.Link
                href="/shop"
                target="_blank"
                rel="noopener noreferrer"
                title="Marketplace: products from stores on iBigDo (opens in a new tab)"
                aria-label="Marketplace, opens in a new tab"
                className="topnav-icon topnav-icon-shop"
                data-testid="marketplace-link"
              >
                <i className="bi bi-shop" aria-hidden="true" />
              </Nav.Link>
              <Nav.Link
                href="/help"
                onClick={(e) => { e.preventDefault(); navigate('/help'); }}
                title={unreadReplies > 0 ? `Help & Support (${unreadReplies} new ${unreadReplies === 1 ? 'reply' : 'replies'})` : 'Help & Support'}
                aria-label={unreadReplies > 0 ? `Help & Support, ${unreadReplies} new ${unreadReplies === 1 ? 'reply' : 'replies'}` : 'Help & Support'}
                className={`topnav-icon topnav-icon-help${location.pathname.startsWith('/help') ? ' active' : ''}`}
                style={{ position: 'relative' }}
              >
                <i className="bi bi-life-preserver" aria-hidden="true" />
                {unreadReplies > 0 && (
                  <span
                    className="badge rounded-pill bg-danger"
                    style={{ position: 'absolute', top: 2, left: 18, fontSize: '0.65rem', padding: '0.25em 0.45em' }}
                  >
                    {unreadReplies > 9 ? '9+' : unreadReplies}
                  </span>
                )}
              </Nav.Link>
              <ThemeToggle />
              <UserMenu />
            </Nav>
          </Container>
        </Navbar>

        {/* Page Content */}
        <div className="main-content">
          <AllowanceBanner />
          <SidebarContext.Provider value={sidebarState}>
            {children}
          </SidebarContext.Provider>
        </div>
      </div>
    </div>
  );
}

export default Layout;



